import React, { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { Col, Row, Form } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
// icons
import { AiOutlineEye } from 'react-icons/ai';
// api
import { getAllEditorials } from 'api/products';
// constants
import { getProductType, getGenders } from 'constants';
//helpers
import { verificateNullValue } from 'helpers';
// components
import { SelectInputCustom } from 'components/input/index';
import CustomInput from 'components/backoffice/customInput';
import ButtonCustom from 'components/backoffice/buttonCustom';
import DeleteModalCustom from 'components/backoffice/modals/DeleteModalCustom';
import CustomCheckbox from 'components/backoffice/customCheckbox';
import Textarea from 'components/backoffice/customInput/textAreaCustom';
import Button from 'components/backoffice/buttonCustom';
import WatchModal from 'components/backoffice/modals/WatchModal';
import SpinnerComponent from 'components/backoffice/SpinnerComponent';
import BookForm from './bookForm';
import MerchandisingForm from './merchandisingForm';
import SearchEntity from '../customInput/SearchEntity';

const ProductForm = ({
  isEditing,
  onSubmitForm,
  fileInputRef,
  onDelete,
  openModalDelete,
  setOpenModalDelete,
  defaultValues,
  loading,
  beforeTitle,
  resetValue,
  errorOnAction,
}) => {
  const { t } = useTranslation(['backoffice']);
  const [openModalImage, setOpenModalImage] = useState(false);
  const [typeProduct, setTypeProduct] = useState('');
  const contenidos = getProductType(t);
  const {
    reset,
    formState: { errors },
    setValue,
    control,
    handleSubmit,
    getValues,
    setError,
  } = useForm();

  const genders = getGenders(t);

  const watchItem = (item) => {
    setValue('cover', item);
    setOpenModalImage(true);
  };

  useEffect(() => {
    if (Object.entries(errorOnAction).length > 0) {
      Object.entries(errorOnAction).forEach((err) => {
        setError(err[0], {
          type: 'manual',
          message: err[1][0],
        });
      });
    }
  }, [errorOnAction, setError]);

  useEffect(() => {
    if (!isEditing && resetValue) reset(defaultValues);
  }, [defaultValues, resetValue, isEditing, reset]);

  const rawDate = defaultValues?.publication_date;
  const validDate =
    rawDate && rawDate !== '1-01-01' ? moment(rawDate, ['YYYY-MM-DD', 'YYYY-M-D']).local().format('YYYY-MM-DD') : null;

  const imageUrl = defaultValues?.cover || defaultValues?.image;

  // Efecto separado para cargar la imagen inicial
  useEffect(() => {
    if (defaultValues && isEditing && imageUrl) {
      fetch(imageUrl)
        .then((response) => response.blob())
        .then((blob) => {
          const name = imageUrl.split('?')[0].split('/').pop();
          const file = new File([blob], name, { type: `image/${name.split('.').pop()}` });
          setValue('cover', file);
        })
        .catch((error) => {
          console.error('Error al obtener la imagen:', error);
        });
    }
  }, [defaultValues, isEditing, imageUrl, setValue]);

  // Efecto separado para cargar los demás campos del formulario
  useEffect(() => {
    if (defaultValues) {
      if (isEditing) {
        const defaultProductType =
          contenidos.find((item) => item.id === defaultValues?.type) || defaultValues?.product_type;
        setValue('ean', verificateNullValue(defaultValues?.ean));
        setValue('num_paginas', verificateNullValue(defaultValues?.num_paginas));
        setValue('num_edicion', verificateNullValue(defaultValues?.num_edicion));
        setValue('alto_cm', verificateNullValue(defaultValues?.alto_cm));
        setValue('ancho_cm', verificateNullValue(defaultValues?.ancho_cm));
        setValue('grosor_cm', verificateNullValue(defaultValues?.grosor_cm));
        setValue('peso', verificateNullValue(Math.trunc(defaultValues?.peso)));
        setValue(
          'ibic',
          Array.isArray(defaultValues?.materia_ibic)
            ? defaultValues.materia_ibic.map((item) => ({
                value: item.id,
                label: `${item.codigo} (${item.descripcion})`,
              }))
            : [],
        );
        setValue(
          'bookbindings',
          Array.isArray(defaultValues?.encuadernacion)
            ? defaultValues.encuadernacion.map((item) => ({
                value: item.id,
                label: item.descripcion,
              }))
            : [],
        );
        setValue('options', Array.isArray(defaultValues?.options) ? defaultValues.options : []);
        setValue('titulo', verificateNullValue(defaultValues?.titulo));
        setValue('titulo_org', defaultValues?.titulo_orig);
        setValue('caratula', defaultValues?.caratula);
        setValue('texto_resumen', defaultValues?.texto_resumen);
        setValue('num_en_coleccion', defaultValues?.num_en_coleccion);
        setValue('precio_sin_iva', defaultValues?.precio_sin_iva);
        setValue('precio_venta_publico', defaultValues?.precio_venta_publico);
        setValue('iva', defaultValues?.iva);
        setValue('ubicacion_stock', defaultValues?.ubicacion_stock);
        setValue('editorial', { value: defaultValues?.editorial?.id, label: defaultValues?.editorial?.nombre_fiscal });
        setValue('genero', defaultValues?.genero);
        setValue('publicada', defaultValues?.publicada);
        setValue('product_type', defaultProductType);
        setTypeProduct(defaultProductType?.id);
        setValue(
          'authors',
          Array.isArray(defaultValues?.authors)
            ? defaultValues.authors.map((item) => ({
                value: item.id,
                label: item.nombre_autor,
              }))
            : [],
        );
        setValue('publication_date', validDate);
        setValue(
          'languages',
          Array.isArray(defaultValues?.languages)
            ? defaultValues.languages.map((item) => ({
                value: item.id,
                label: item.nombre,
              }))
            : [],
        );
        if (defaultValues?.availabled_status) {
          let companyHasStock = {};
          let availabledStatus = defaultValues.availabled_status;
          
          if (typeof availabledStatus === 'string') {
            try {
              availabledStatus = JSON.parse(availabledStatus);
            } catch (e) {
              console.error('Error parsing availabled_status:', e);
              availabledStatus = [];
            }
          }
          
          if (Array.isArray(availabledStatus)) {
            availabledStatus.forEach(item => {
              if (item.code) {
                companyHasStock[item.code] = item.status === 2;
              }
            });
          } 
          else if (typeof availabledStatus === 'object') {
            Object.entries(availabledStatus).forEach(([code, value]) => {
              if (value === 2 || value === true) {
                companyHasStock[code] = true;
              } else if (value === 1 || value === false) {
                companyHasStock[code] = false;
              } else {
                companyHasStock[code] = value;
              }
            });
          }
          
          setValue('availabled_status', companyHasStock);
        }
      } else {
        if (!typeProduct) {
          const defaultProductType = contenidos.find((item) => item.id === 'book') || contenidos[0];
          setValue('product_type', defaultProductType);
          setTypeProduct(defaultProductType?.id);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValues, isEditing]);

  let item;
  const image = getValues('cover');

  if (isEditing) {
    item =
      typeof image === 'string'
        ? image
        : image instanceof Blob || image instanceof File
          ? URL.createObjectURL(image)
          : '';
  } else {
    item = image !== undefined ? URL.createObjectURL(image) : '';
  }

  const getTypeProductComponent = () => {
    switch (typeProduct) {
      case 'merchandising':
        return <MerchandisingForm control={control} merchandising={defaultValues} setValue={setValue} />;
      case 'book':
      default:
        return <BookForm control={control} book={defaultValues} setValue={setValue} />;
    }
  };

  return (
    <div className="mt-5">
      <Form onSubmit={handleSubmit(onSubmitForm)}>
        <Row className="mt-4">
          <Col sm={4}>
            <Controller
              name="ean"
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('ean')}
                  onChange={(e) => onChange(e)}
                  placeholder={t('ean')}
                  value={value}
                />
              )}
            />
            {errors?.ean && <small className="text-danger ">{errors.ean.message}</small>}
          </Col>
          <Col sm={4} className="mt-4 mt-sm-0">
            <Controller
              name="titulo"
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  onChange={(e) => onChange(e)}
                  label={t('titulo')}
                  placeholder={t('titulo')}
                  value={value}
                />
              )}
            />
            {errors?.titulo && <small className="text-danger ">{errors.titulo.message}</small>}
          </Col>
          <Col sm={4} className="mt-4 mt-sm-0">
            <Controller
              name="titulo_org"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('titulooriginal')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('titulooriginal')}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={4}>
            <Controller
              name="caratula"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  label={t('caratula')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('caratula')}
                />
              )}
            />
          </Col>
          <Col sm={4} className="mt-4 mt-sm-0">
            <Controller
              name="num_en_coleccion"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  disabled={false}
                  min="0"
                  label={t('numerocoleccion')}
                  onChange={(value) => {
                    if (Number(value) < 0 || isNaN(Number(value))) {
                      onChange(0);
                    } else {
                      onChange(value);
                    }
                  }}
                  value={value}
                  placeholder={t('numerocoleccion')}
                />
              )}
            />
            {errors?.num_en_coleccion && <small className="text-danger ">{errors.num_en_coleccion.message}</small>}
            
          </Col>
          <Col sm={4} className="mt-4 mt-sm-0">
            <Controller
              name="genero"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <SelectInputCustom
                  choices={genders}
                  label={t('audience')}
                  value={value}
                  placeholder={t('select')}
                  onCheckedItem={(e) => onChange(e)}
                  className="mt-0"
                />
              )}
            />
            {errors.genero && <small className="text-danger ">{errors.genero.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={4}>
            <Controller
              name="precio_sin_iva"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  min="0"
                  disabled={false}
                  onChange={(value) => {
                    if (Number(value) < 0 || isNaN(Number(value))) {
                      onChange(0);
                    } else {
                      onChange(value);
                    }
                  }}
                  label={t('preciosiniva')}
                  placeholder={t('preciosiniva')}
                  value={value}
                />
              )}
            />
            {errors.precio_sin_iva && <small className="text-danger ">{errors.precio_sin_iva.message}</small>}
          </Col>
          <Col sm={4} className="mt-4 mt-sm-0">
            <Controller
              name="precio_venta_publico"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  min="0"
                  disabled={false}
                  onChange={(value) => {
                    if (Number(value) < 0 || isNaN(Number(value))) {
                      onChange(0);
                    } else {
                      onChange(value);
                    }
                  }}
                  label={t('precioventapublico')}
                  placeholder={t('precioventapublico')}
                  value={value}
                />
              )}
            />
            {errors.precio_venta_publico && (
              <small className="text-danger ">{errors.precio_venta_publico.message}</small>
            )}
          </Col>
          <Col sm={4} className="mt-4 mt-sm-0">
            <Controller
              name="iva"
              control={control}
              rules={{
                required: {
                  value: true,
                  message: t('camporequerido'),
                },
              }}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="number"
                  min="0"
                  disabled={false}
                  label={t('iva')}
                  onChange={(value) => {
                    if (Number(value) < 0 || isNaN(Number(value))) {
                      onChange(0);
                    } else {
                      onChange(value);
                    }
                  }}
                  placeholder={t('iva')}
                  value={value}
                />
              )}
            />
            {errors.iva && <small className="text-danger ">{errors.iva.message}</small>}
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={4}>
            <Controller
              name="ubicacion_stock"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomInput
                  type="text"
                  disabled={false}
                  label={t('ubicacionstock')}
                  onChange={(e) => onChange(e)}
                  value={value}
                  placeholder={t('ubicacionstock')}
                />
              )}
            />
          </Col>
          <Col sm={4} className="d-flex mt-4 mt-sm-0">
            <div className="position-relative w-100">
              <Controller
                name="cover"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomInput
                    type="file"
                    dropFileMsg={t('dropFile')}
                    label={t('imagen')}
                    placeholder={t('selectImg')}
                    accept="image/*"
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    ref={fileInputRef}
                  />
                )}
              />
            </div>
            <div className="d-flex ms-3 mx-sm-3 mx-0 align-bottom">
              <Button color={'terciary'} type="button" onClick={() => watchItem(getValues('cover'))}>
                <AiOutlineEye color="white" />
              </Button>
            </div>
          </Col>
          <Col md={4} className="select mt-4 mt-sm-0">
            <Form.Label className="text-light">{t('product.editorial')}</Form.Label>
            <Controller
              control={control}
              name="editorial"
              render={({ field: { onChange, value } }) => (
                <SearchEntity
                  fetchData={getAllEditorials}
                  filterField="nombre_fiscal"
                  labelField="nombre_fiscal"
                  valueField="id"
                  onSelected={onChange}
                  defaultValue={value}
                  placeholder={t('product.editorial')}
                  isMulti={false}
                  formatCustomLabel={false}
                />
              )}
            />
          </Col>
        </Row>
        <Row className="mt-4">
          <Col sm={4}>
            <Form.Label className="text-light">{t('resumentexto')}</Form.Label>
            <Controller
              name="texto_resumen"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Textarea value={value} onChange={(e) => onChange(e)} placeholder={t('resumentexto')} />
              )}
            />
          </Col>
          <Col sm={4}>
            <Row>
              <Col sm={6} className="d-flex align-items-center mt-3">
                <Controller
                  name="publicada"
                  control={control}
                  render={({ field: { onChange, value } }) => {
                    return (
                      <CustomCheckbox
                        id="publicada"
                        label={`${t('publicado')}`}
                        checked={value}
                        onChange={(e) => onChange(e.target.checked)}
                      />
                    );
                  }}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Col sm={4} className="mt-4">
          <Controller
            name="product_type"
            control={control}
            rules={{
              required: {
                value: true,
                message: t('camporequerido'),
              },
            }}
            render={({ field: { onChange, value } }) =>
              isEditing ? (
                <CustomInput
                  type="text"
                  readOnly
                  onChange={onChange}
                  label={`${t('product.producttype')}`}
                  value={value?.value}
                />
              ) : (
                <SelectInputCustom
                  choices={contenidos}
                  label={t('product.producttype')}
                  onCheckedItem={(e) => {
                    setTypeProduct(e);
                    onChange(e);
                  }}
                  value={value}
                  className="mt-0"
                />
              )
            }
          />
          {errors.product_type && <small className="text-danger ">{errors.product_type.message}</small>}
        </Col>
        <div className="w-100 mt-3 border-grey" />
        <Row>
          <Col sm={12}>{getTypeProductComponent()}</Col>
        </Row>
      </Form>
      <div className="my-4 d-flex justify-content-end gap-3">
        <ButtonCustom color="primary" onClick={handleSubmit(onSubmitForm)} loading={loading}>
          {t('save')}
          <SpinnerComponent loading={loading} />
        </ButtonCustom>
        {isEditing && (
          <ButtonCustom color="secondary" onClick={() => setOpenModalDelete(true)} loading={loading}>
            {t('eliminar')}
          </ButtonCustom>
        )}
      </div>
      <DeleteModalCustom
        openModal={openModalDelete}
        setOpenModal={setOpenModalDelete}
        handleDelete={onDelete}
        deleteTitle={t('deleteproduct')}
        beforeTitle={defaultValues?.titulo}
      />
      <WatchModal
        item={item}
        title={t('imagen')}
        show={openModalImage}
        setShow={setOpenModalImage}
        beforeTitle={beforeTitle ? beforeTitle : t('edit', { title: defaultValues?.title })}
      />
    </div>
  );
};

ProductForm.propTypes = {
  onSubmitForm: PropTypes.func.isRequired,
  defaultValues: PropTypes.object,
  isEditing: PropTypes.bool.isRequired,
  openModalDelete: PropTypes.bool,
  setOpenModalDelete: PropTypes.func,
  onDelete: PropTypes.func,
  id: PropTypes.any,
  loading: PropTypes.bool,
  beforeTitle: PropTypes.string,
  resetValue: PropTypes.bool,
  errorOnAction: PropTypes.any,
};

export default ProductForm;
